import { useState, useEffect } from "react";
import { Star, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AnimateIn } from "@/hooks/useAnimations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewSection() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setReviews(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, form);
      setStatus({ type: "success", msg: data.message });
      setForm({ name: "", email: "", rating: 5, message: "" });
      setShowForm(false);
    } catch (err) {
      setStatus({ type: "error", msg: "Failed to submit. Please try again." });
    }
    setLoading(false);
  };

  return (
    <section id="reviews" data-testid="review-section" className="py-14 sm:py-20 lg:py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimateIn>
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-red-600 mb-4">Reviews</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mb-6">
              Client <span className="text-red-600">Reviews</span>
            </h2>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="toggle-review-form"
            className="bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-8 border border-transparent hover:bg-white hover:text-black transition-colors duration-200 text-xs"
          >
            {showForm ? "Close Form" : "Write a Review"}
          </button>
        </div>
        </AnimateIn>

        {showForm && (
          <div className="max-w-lg mx-auto mb-16 border border-white/10 bg-[#141414] p-8 animate-fadeInUp">
            <form onSubmit={handleSubmit} data-testid="review-form" className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Your Name</Label>
                <Input
                  data-testid="review-name"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  required
                  placeholder="Your name"
                  className="bg-transparent border-b-2 border-white/20 rounded-none px-0 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Email</Label>
                <Input
                  type="email"
                  data-testid="review-email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  required
                  placeholder="your@email.com"
                  className="bg-transparent border-b-2 border-white/20 rounded-none px-0 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Rating</Label>
                <div className="flex gap-2" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({...form, rating: n})}
                      className="p-1"
                    >
                      <Star
                        size={24}
                        className={`transition-colors ${n <= form.rating ? "text-red-500 fill-red-500" : "text-zinc-600"}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Your Review</Label>
                <Textarea
                  data-testid="review-message"
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  required
                  rows={3}
                  placeholder="Share your experience..."
                  className="bg-transparent border-2 border-white/20 rounded-none px-3 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="review-submit"
                className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-8 border border-transparent hover:bg-white hover:text-black transition-colors duration-200 text-xs disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Submitting..." : <><Send size={14} /> Submit Review</>}
              </button>
            </form>
          </div>
        )}

        {status && (
          <div data-testid="review-status" className={`max-w-lg mx-auto mb-8 p-4 text-sm text-center ${status.type === "success" ? "bg-green-600/10 text-green-400 border border-green-600/20" : "bg-red-600/10 text-red-400 border border-red-600/20"}`}>
            {status.msg}
          </div>
        )}

        {reviews.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <div key={r.id || i} data-testid={`review-card-${i}`} className="border border-white/10 bg-[#141414] p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(r.rating || 5)].map((_, j) => (
                    <Star key={j} size={12} className="text-red-500 fill-red-500" />
                  ))}
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">&ldquo;{r.message}&rdquo;</p>
                <p className="text-xs font-bold text-white uppercase tracking-wider">{r.name}</p>
              </div>
            ))}
          </div>
        )}

        {reviews.length === 0 && !showForm && (
          <div className="max-w-xl mx-auto mt-8 border border-white/10 bg-[#111] p-8 sm:p-10 text-center card-accent card-top-bar group hover:border-red-600/60 transition-all duration-300">
            <div className="flex justify-center gap-1 mb-4">
              {[0,1,2,3,4].map(i => (
                <Star key={i} size={18} className="text-red-500/30" />
              ))}
            </div>
            <p className="text-white font-bold uppercase tracking-wider text-sm mb-2">Be The First Voice</p>
            <p className="text-zinc-500 text-sm leading-relaxed">
              We&rsquo;re a young startup and every review helps future clients trust our craft. If you&rsquo;ve worked with us, we&rsquo;d love to hear about your experience.
            </p>
            <button
              onClick={() => setShowForm(true)}
              data-testid="empty-review-cta"
              className="mt-5 text-[11px] font-bold uppercase tracking-widest text-red-600 hover:text-white transition-colors link-underline"
            >
              Share your story &rarr;
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
